import React from 'react';
import { Trash2 } from 'lucide-react';
import Button from './Button';
import { ProductionTask } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';

interface TimelineViewProps {
  tasks: ProductionTask[];
  onEditTask: (task: ProductionTask) => void;
  onDeleteTask: (id: string) => void;
  getSupplierName: (id: string | null) => string;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  getSupplierName,
}) => {
  // Sort tasks by start date
  const sortedTasks = [...tasks].sort((a, b) => {
    // If both have start dates, compare them
    if (a.start_date && b.start_date) {
      return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    }
    
    // If only one has a start date, prioritize the one with a date
    if (a.start_date) return -1;
    if (b.start_date) return 1;
    
    // If neither has a start date, sort by name
    return a.task_name.localeCompare(b.task_name);
  });

  // Function to get task duration in days
  const getTaskDuration = (task: ProductionTask) => {
    if (!task.start_date || !task.end_date) return 0;
    
    const start = new Date(task.start_date);
    const end = new Date(task.end_date);
    
    // Calculate difference in days
    return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
  };

  // Function to get task status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700';
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700';
      case 'finished':
        return 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700';
    }
  };

  // Function to get task status text color
  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-amber-800 dark:text-amber-200';
      case 'in_progress':
        return 'text-blue-800 dark:text-blue-200';
      case 'finished':
        return 'text-green-800 dark:text-green-200';
      default:
        return 'text-gray-800 dark:text-gray-200';
    }
  };

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No hay tareas registradas para esta producción
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-max">
        {sortedTasks.map((task) => (
          <div 
            key={task.id} 
            className="mb-4 relative"
            onClick={() => onEditTask(task)}
            style={{ cursor: 'pointer' }}
          >
            <div className={`
              p-4 rounded-lg border ${getStatusColor(task.status)}
              hover:shadow-md transition-shadow
            `}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{task.task_name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Proveedor: {getSupplierName(task.supplier_id)}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fecha Inicio:</p>
                      <p className="text-sm font-medium">
                        {task.start_date 
                          ? formatArgentinaDate(task.start_date) 
                          : 'Fecha no asignada'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fecha Fin:</p>
                      <p className="text-sm font-medium">
                        {task.end_date 
                          ? formatArgentinaDate(task.end_date) 
                          : 'Fecha no asignada'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Duración:</p>
                      <p className="text-sm font-medium">
                        {task.start_date && task.end_date 
                          ? `${getTaskDuration(task)} días` 
                          : 'No definida'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Costo:</p>
                      <p className="text-sm font-medium">{formatCurrency(task.cost)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className={`
                    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2
                    ${task.status === 'pending' 
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
                      : task.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                      : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    }
                  `}>
                    {task.status === 'pending' 
                      ? 'Pendiente' 
                      : task.status === 'in_progress' 
                      ? 'En Proceso' 
                      : 'Terminado'}
                  </span>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onClick
                        onDeleteTask(task.id);
                      }}
                    >
                      
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineView;